import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { CheckCircle2, ExternalLink, FileCheck2, RefreshCw, Wallet, XCircle } from 'lucide-react';
import { API, toast } from '@/App';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { formatPaymentMethodLabel, formatUiLabel } from '@/utils/displayLabels';
import { getPricingSummary } from '@/utils/promoCodes';

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString('fr-FR');
};

const statusBadgeClass = (status) => {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'paid' || normalized === 'active') return 'bg-green-100 text-green-700';
  if (normalized === 'rejected') return 'bg-red-100 text-red-700';
  return 'bg-amber-100 text-amber-700';
};

const renderProofLink = (url) => {
  if (!url) {
    return <span className="text-xs text-gray-400">Aucune preuve</span>;
  }
  return (
    <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-cyan-700 hover:underline">
      Voir la preuve
      <ExternalLink className="w-3 h-3" />
    </a>
  );
};

const renderPricingCell = (record) => {
  const summary = getPricingSummary(record, {
    fallbackAmount: record?.amount || 0,
    fallbackCurrency: record?.currency || 'FCFA',
  });
  return (
    <div className="text-sm space-y-1">
      <div className="flex items-center justify-between gap-3">
        <span className="text-gray-500">Brut</span>
        <span>{summary.amountBefore.toLocaleString()} {summary.currency}</span>
      </div>
      {summary.hasReferralDiscount && (
        <div className="flex items-center justify-between gap-3 text-emerald-700">
          <span>Parrainage{summary.referralDiscountPercent ? ` (${summary.referralDiscountPercent}%)` : ''}</span>
          <span>- {summary.referralDiscountAmount.toLocaleString()} {summary.currency}</span>
        </div>
      )}
      {summary.hasPromoDiscount && (
        <div className="flex items-center justify-between gap-3 text-green-700">
          <span>Promo{summary.promoCode ? ` (${summary.promoCode})` : ''}</span>
          <span>- {summary.promoDiscountAmount.toLocaleString()} {summary.currency}</span>
        </div>
      )}
      <div className="flex items-center justify-between gap-3 font-semibold">
        <span>Net</span>
        <span>{summary.amountAfter.toLocaleString()} {summary.currency}</span>
      </div>
    </div>
  );
};

const PaymentValidationManagement = () => {
  const [deliveryPayments, setDeliveryPayments] = useState([]);
  const [walletRecharges, setWalletRecharges] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submittingKey, setSubmittingKey] = useState('');
  const [decisionDialog, setDecisionDialog] = useState({
    open: false,
    resourceType: '',
    action: 'reject',
    resourceId: '',
    label: '',
    reason: '',
  });

  const pendingStats = useMemo(() => ({
    delivery: deliveryPayments.length,
    wallet: walletRecharges.length,
    subscription: subscriptions.length,
  }), [deliveryPayments, walletRecharges, subscriptions]);

  const loadPendingPayments = async () => {
    const [deliveryResponse, walletResponse, subscriptionResponse] = await Promise.all([
      axios.get(`${API}/admin/payments`, { params: { status: 'pending_validation' } }),
      axios.get(`${API}/admin/wallet-recharges`, { params: { status: 'pending_validation' } }),
      axios.get(`${API}/admin/subscriptions`),
    ]);

    setDeliveryPayments(deliveryResponse.data || []);
    setWalletRecharges(walletResponse.data || []);
    setSubscriptions(
      (subscriptionResponse.data || []).filter((item) => (
        String(item.payment_validation_status || '').toLowerCase() === 'pending_validation'
        && ['mtn_money', 'airtel_money', 'orange_money', 'mpesa'].includes(String(item.payment_method || '').toLowerCase())
      ))
    );
  };

  const refreshAll = async () => {
    setLoading(true);
    try {
      await loadPendingPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossible de charger les validations de paiement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshAll();
  }, []);

  const openRejectDialog = (resourceType, resourceId, label) => {
    setDecisionDialog({
      open: true,
      resourceType,
      action: 'reject',
      resourceId,
      label,
      reason: '',
    });
  };

  const closeDecisionDialog = () => {
    setDecisionDialog((prev) => ({ ...prev, open: false, reason: '' }));
  };

  const submitDecision = async ({ resourceType, resourceId, action, reason, note }) => {
    const requestKey = `${resourceType}:${action}:${resourceId}`;
    setSubmittingKey(requestKey);
    try {
      if (resourceType === 'delivery') {
        if (action === 'approve') {
          await axios.post(`${API}/admin/payments/${resourceId}/approve`, { note });
        } else {
          await axios.post(`${API}/admin/payments/${resourceId}/reject`, { reason });
        }
      } else if (resourceType === 'wallet') {
        if (action === 'approve') {
          await axios.post(`${API}/admin/wallet-recharges/${resourceId}/approve`, { note });
        } else {
          await axios.post(`${API}/admin/wallet-recharges/${resourceId}/reject`, { reason });
        }
      } else if (resourceType === 'subscription') {
        if (action === 'approve') {
          await axios.patch(`${API}/admin/subscriptions/${resourceId}/activate`, { note });
        } else {
          await axios.post(`${API}/admin/subscriptions/${resourceId}/reject`, { reason });
        }
      }

      toast.success(action === 'approve' ? 'Paiement valide avec succes' : 'Paiement rejete avec succes');
      closeDecisionDialog();
      await refreshAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation de validation impossible');
    } finally {
      setSubmittingKey('');
    }
  };

  const renderDeliveryRows = () => (
    deliveryPayments.length === 0 ? (
      <div className="text-sm text-gray-500 py-8">Aucun paiement de livraison en attente.</div>
    ) : (
      <Table>
        <TableHeader>
            <TableRow>
              <TableHead>Livraison</TableHead>
              <TableHead>Vendeur</TableHead>
              <TableHead>Montants</TableHead>
              <TableHead>Methode</TableHead>
              <TableHead>Preuve</TableHead>
              <TableHead>Date</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {deliveryPayments.map((item) => (
            <TableRow key={item.id}>
              <TableCell>
                <div className="text-sm">
                  <p className="font-medium">{item.delivery?.tracking_code || item.delivery_id}</p>
                  <p className="text-gray-500">{item.delivery_id}</p>
                </div>
              </TableCell>
              <TableCell>
                <div className="text-sm">
                  <p className="font-medium">{item.vendor?.full_name || item.vendeur_name || '-'}</p>
                  <p className="text-gray-500">{item.vendor?.email || '-'}</p>
                </div>
              </TableCell>
              <TableCell>{renderPricingCell(item)}</TableCell>
              <TableCell><Badge variant="outline">{formatPaymentMethodLabel(item.method || item.payment_method)}</Badge></TableCell>
              <TableCell>{renderProofLink(item.proof_access_url)}</TableCell>
              <TableCell>{formatDateTime(item.created_at)}</TableCell>
              <TableCell>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="bg-green-600 hover:bg-green-700"
                    disabled={submittingKey === `delivery:approve:${item.id}`}
                    onClick={() => submitDecision({ resourceType: 'delivery', resourceId: item.id, action: 'approve', note: '' })}
                  >
                    <CheckCircle2 className="w-4 h-4 mr-1" />
                    Valider
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => openRejectDialog('delivery', item.id, item.delivery?.tracking_code || item.delivery_id)}>
                    <XCircle className="w-4 h-4 mr-1" />
                    Rejeter
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    )
  );

  const renderWalletRows = () => (
    walletRecharges.length === 0 ? (
      <div className="text-sm text-gray-500 py-8">Aucune recharge wallet en attente.</div>
    ) : (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Recharge</TableHead>
            <TableHead>Vendeur</TableHead>
            <TableHead>Montant</TableHead>
            <TableHead>Methode</TableHead>
            <TableHead>Preuve</TableHead>
            <TableHead>Date</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {walletRecharges.map((item) => (
            <TableRow key={item.id}>
              <TableCell className="text-sm">
                <p className="font-medium">{item.id}</p>
                <p className="text-gray-500">{item.org_id}</p>
              </TableCell>
              <TableCell>
                <div className="text-sm">
                  <p className="font-medium">{item.user?.full_name || '-'}</p>
                  <p className="text-gray-500">{item.user?.email || '-'}</p>
                </div>
              </TableCell>
              <TableCell>{Number(item.amount || 0).toLocaleString()} {item.currency || 'FCFA'}</TableCell>
              <TableCell><Badge variant="outline">{formatPaymentMethodLabel(item.method)}</Badge></TableCell>
              <TableCell>{renderProofLink(item.proof_access_url)}</TableCell>
              <TableCell>{formatDateTime(item.created_at)}</TableCell>
              <TableCell>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="bg-green-600 hover:bg-green-700"
                    disabled={submittingKey === `wallet:approve:${item.id}`}
                    onClick={() => submitDecision({ resourceType: 'wallet', resourceId: item.id, action: 'approve', note: '' })}
                  >
                    <CheckCircle2 className="w-4 h-4 mr-1" />
                    Valider
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => openRejectDialog('wallet', item.id, item.id)}>
                    <XCircle className="w-4 h-4 mr-1" />
                    Rejeter
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    )
  );

  const renderSubscriptionRows = () => (
    subscriptions.length === 0 ? (
      <div className="text-sm text-gray-500 py-8">Aucun abonnement en attente de validation.</div>
    ) : (
      <Table>
        <TableHeader>
            <TableRow>
              <TableHead>Vendeur</TableHead>
              <TableHead>Plan</TableHead>
              <TableHead>Montants</TableHead>
              <TableHead>Methode</TableHead>
              <TableHead>Preuve</TableHead>
              <TableHead>Statut</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {subscriptions.map((item) => (
            <TableRow key={item.id}>
              <TableCell>
                <div className="text-sm">
                  <p className="font-medium">{item.vendor?.full_name || item.vendeur_name || '-'}</p>
                  <p className="text-gray-500">{item.vendor?.email || '-'}</p>
                </div>
              </TableCell>
              <TableCell>{formatUiLabel(item.plan)}</TableCell>
              <TableCell>{renderPricingCell(item)}</TableCell>
              <TableCell><Badge variant="outline">{formatPaymentMethodLabel(item.payment_method)}</Badge></TableCell>
              <TableCell>{renderProofLink(item.proof_access_url)}</TableCell>
              <TableCell><Badge className={statusBadgeClass(item.payment_validation_status || item.status)}>{formatUiLabel(item.payment_validation_status || item.status)}</Badge></TableCell>
              <TableCell>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="bg-green-600 hover:bg-green-700"
                    disabled={submittingKey === `subscription:approve:${item.id}`}
                    onClick={() => submitDecision({ resourceType: 'subscription', resourceId: item.id, action: 'approve', note: '' })}
                  >
                    <CheckCircle2 className="w-4 h-4 mr-1" />
                    Activer
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => openRejectDialog('subscription', item.id, item.id)}>
                    <XCircle className="w-4 h-4 mr-1" />
                    Rejeter
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    )
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Validations paiements</h2>
          <p className="text-sm text-gray-600">Validez les preuves Mobile Money pour les livraisons, les recharges wallet et les abonnements.</p>
        </div>
        <Button variant="outline" onClick={refreshAll} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Paiements livraison</CardDescription>
            <CardTitle className="text-3xl">{pendingStats.delivery}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Recharges wallet</CardDescription>
            <CardTitle className="text-3xl">{pendingStats.wallet}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Abonnements Pro</CardDescription>
            <CardTitle className="text-3xl">{pendingStats.subscription}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileCheck2 className="w-5 h-5 text-red-600" />
            Paiements en attente de validation
          </CardTitle>
          <CardDescription>Chaque validation ou rejet ecrit un log d audit et notifie le vendeur concerne.</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="delivery">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="delivery">Livraisons ({pendingStats.delivery})</TabsTrigger>
              <TabsTrigger value="wallet">Wallet ({pendingStats.wallet})</TabsTrigger>
              <TabsTrigger value="subscription">Abonnements ({pendingStats.subscription})</TabsTrigger>
            </TabsList>
            <TabsContent value="delivery" className="mt-4">{renderDeliveryRows()}</TabsContent>
            <TabsContent value="wallet" className="mt-4">{renderWalletRows()}</TabsContent>
            <TabsContent value="subscription" className="mt-4">{renderSubscriptionRows()}</TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <Dialog open={decisionDialog.open} onOpenChange={(open) => (!open ? closeDecisionDialog() : null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rejeter cette preuve de paiement</DialogTitle>
            <DialogDescription>
              Ajoutez un motif clair pour {decisionDialog.label || decisionDialog.resourceId}. Ce message sera visible par l utilisateur.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="payment-rejection-reason">Motif du rejet</Label>
            <Textarea
              id="payment-rejection-reason"
              rows={4}
              value={decisionDialog.reason}
              onChange={(event) => setDecisionDialog((prev) => ({ ...prev, reason: event.target.value }))}
              placeholder="Ex: capture floue, montant incoherent, numero non visible..."
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDecisionDialog}>Annuler</Button>
            <Button
              variant="destructive"
              onClick={() => submitDecision({
                resourceType: decisionDialog.resourceType,
                resourceId: decisionDialog.resourceId,
                action: 'reject',
                reason: decisionDialog.reason.trim(),
              })}
              disabled={!decisionDialog.reason.trim() || submittingKey === `${decisionDialog.resourceType}:reject:${decisionDialog.resourceId}`}
            >
              Rejeter la preuve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentValidationManagement;
